import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { Typography } from 'antd';

const { Text } = Typography;

// Chart.js v3: `horizontalBar` type removed -> use type 'bar' with
// `indexAxis: 'y'`. Rounded corners use the native `borderRadius` option.
// Components/elements/scales are registered once in src/utils/chartSetup.js.
const HorizontalBar = ({ data, color = '#8884d8', legendLabel = 'Frequency' }) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) {
            return () => {}; // Return empty cleanup function
        }

        const chartOptions = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                },
                datalabels: {
                    anchor: 'center',
                    align: 'center',
                    color: 'white',
                    font: {
                        weight: 'regular',
                        size: 12
                    },
                    formatter: (value) => {
                        return value.toLocaleString();
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label(context) {
                            const label = context.dataset.label || '';
                            const value = context.parsed.x.toLocaleString();
                            return `${label}: ${value}`;
                        }
                    }
                },
            },
            scales: {
                x: {
                    display: true,
                    grid: {
                        display: true
                    },
                    border: {
                        dash: [3, 3],
                    },
                    beginAtZero: true,
                    ticks: {
                        callback(value) {
                            return value.toLocaleString();
                        }
                    },
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        font: {
                            size: 12
                        }
                    }
                },
            },
        };

        // Create chart instance
        chartInstanceRef.current = new ChartJS(canvasRef.current, {
            type: 'bar',
            data: {
                labels: [],
                datasets: [],
            },
            options: chartOptions,
        });

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        if (!chartInstanceRef.current || !data || data.length === 0) return;

        // Update chart data
        chartInstanceRef.current.data.labels = data.map(d => d.label);
        chartInstanceRef.current.data.datasets = [{
            label: legendLabel,
            data: data.map(d => d.value),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
            borderRadius: 6,
        }];

        chartInstanceRef.current.update();
    }, [data, color, legendLabel]);

    if (!data || data.length === 0) {
        return <Text type="secondary">No data available</Text>;
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '300px' }}>
            <canvas ref={canvasRef} />
        </div>
    );
};

export default HorizontalBar;
